'use client';

import {
  createCommendaOSEntity,
  executeCommendaOS,
  fetchEntitySchema,
  findFieldByReadPath,
  updateCommendaOSEntity,
  upsertCommendaOSEntity,
} from '@commenda/api/commenda-os-dsl';
import { useApiScope } from '@commenda/api/scope';
import type {
  CommendaOSFilter,
  ContextBinding,
  FormDSL,
} from '@commenda/types';
import type {
  DSLContext,
  FieldValue,
} from '@commenda/types/src/commenda-os-dsl';
import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DSLContextMarker } from '../dsl-constants';
import { STANDARD_CONTEXT_KEYS } from '../types';
import {
  type CommendaOSFieldMapping,
  extractBusinessEntitySelectOrCreateComponents,
  extractCommendaOSFields,
  extractDirectorDocumentMappings,
  extractEntitySelectOrCreateComponents,
  extractKeyPersonsComponents,
} from '../utils/commenda-os-extractors';
import { CommendaOSFailureHelper } from '../utils/commenda-os-failure-helper';
import { CommendaOSReadHelper } from '../utils/commenda-os-read-helper';
import { normalizeFiscalYearValue } from '../utils/fiscal-year';

// =============================================================================
// Types
// =============================================================================

interface UseCommendaOSPrefillOptions {
  dsl: FormDSL;
  dslContext?: DSLContext;
  /** Current form data - used for context bindings and to avoid overwriting existing values */
  formData?: Record<string, unknown>;
  /** Called when prefill is complete */
  onPrefillComplete?: (data: Record<string, unknown>) => void;
}

interface UseCommendaOSSubmitOptions {
  dsl: FormDSL;
  dslContext?: DSLContext;
  /** Form data to submit */
  formData: Record<string, unknown>;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Maps entity names to their actual context key when it differs from the
 * default convention of `${entity}Id`. The backend entity definitions use
 * `serviceConfig.idContextKey` for this; this map mirrors those overrides
 * so the frontend sends the correct context keys.
 */
const ENTITY_ID_CONTEXT_KEY: Record<string, string> = {
  keyPerson: 'directorId',
  keyPersonRelationship: 'directorId',
};

function getEntityIdKey(entity: string): string {
  return ENTITY_ID_CONTEXT_KEY[entity] ?? `${entity}Id`;
}

/**
 * Ids created earlier in a submit that have to be written into a later entity's
 * own fields (not just its context).
 *
 * Shape: the outer key is the entity being created; `fromEntity` is an entity
 * created earlier in the same submit; `fieldPath` is the field on the outer
 * entity that the `fromEntity` id is injected into.
 *
 * Example: creating a `transferPricingPolicy` alongside a
 * `transferPricingBenchmark` writes the new benchmark's id into the policy's
 * `benchmarkId` field.
 *
 * This only works because `ENTITY_PRIORITY` (inside `submit` below) sorts
 * `fromEntity` ahead of the entity that consumes it — `transferPricingBenchmark`
 * is priority 0 and `transferPricingPolicy` priority 1 — so the id already
 * exists in `createdIds` by the time the dependent entity is created. Adding an
 * entry here without the matching `ENTITY_PRIORITY` ordering silently injects
 * nothing.
 */
const CREATED_ID_FIELD_INJECTIONS: Record<
  string,
  Array<{ fromEntity: string; fieldPath: string }>
> = {
  transferPricingPolicy: [
    { fromEntity: 'transferPricingBenchmark', fieldPath: 'benchmarkId' },
  ],
};

const RELATIONSHIP_DOCUMENT_KINDS = new Set([
  'shareCertificate',
  'boardResolution',
  'sharePurchaseAndSaleAgreement',
]);

const FORM_CONTEXT_MARKER = DSLContextMarker.FORM_CONTEXT;
const ENTITY_CONTEXT_MARKER = DSLContextMarker.ENTITY_CONTEXT;

/**
 * Normalize a value based on the field's parsingType and type.
 * This ensures values are in the correct format for the backend.
 */
function normalizeValueForSubmit(
  value: unknown,
  parsingType?: string,
  fieldType?: string
): unknown {
  if (value === undefined || value === null) {
    return value;
  }

  if (fieldType === 'boolean') {
    return value === true || value === 'true';
  }

  switch (parsingType) {
    case 'fiscalYear':
      // Convert DSL object format to "MM-DD" string format
      const normalized = normalizeFiscalYearValue(value);
      if (normalized) {
        // Parse the MM-DD format
        const [monthStr, dayStr] = normalized.split('-');
        const endMonth = parseInt(monthStr, 10);
        const endDay = parseInt(dayStr, 10);

        // Convert fiscal year END to fiscal year START (backend stores START)
        const { fiscalYearStartDate, fiscalYearStartMonth } =
          NonTimezoneSensitiveDate.convertFiscalYearEndToStart(
            endDay,
            endMonth
          );

        // Return as "MM-DD" format
        return `${fiscalYearStartMonth.toString().padStart(2, '0')}-${fiscalYearStartDate.toString().padStart(2, '0')}`;
      }
      return value;

    case 'date':
      // Date values should already be ISO strings, but ensure they are
      if (typeof value === 'string') {
        return value;
      }
      // If it's a Date object, convert to ISO string
      if (value instanceof Date) {
        return value.toISOString().split('T')[0];
      }
      return value;

    default:
      return value;
  }
}

// Valid context keys that the DSL backend accepts
const VALID_CONTEXT_KEYS: readonly string[] = STANDARD_CONTEXT_KEYS;

/**
 * Build effective context by merging global context with bound field values
 * Filters out invalid context keys to prevent backend validation errors
 */
function buildEffectiveContext(
  dslContext: DSLContext,
  contextBindings: ContextBinding[] | undefined,
  formData: Record<string, unknown>
): DSLContext {
  const ctx = { ...dslContext };

  if (contextBindings) {
    for (const binding of contextBindings) {
      const key = binding.contextKey;

      if (binding.sourceFieldId === FORM_CONTEXT_MARKER) {
        const value = ctx[key];
        if (value !== undefined && value !== null) {
          ctx[key] = value;
        }
        continue;
      }

      if (!VALID_CONTEXT_KEYS.includes(key)) {
        console.warn(
          `[buildEffectiveContext] Skipping invalid context key: ${key}`
        );
        continue;
      }

      const fieldValue = formData[binding.sourceFieldId];
      if (
        fieldValue !== undefined &&
        fieldValue !== null &&
        fieldValue !== ''
      ) {
        ctx[key] =
          typeof fieldValue === 'string' && !isNaN(Number(fieldValue))
            ? Number(fieldValue)
            : (fieldValue as string | number);
      }
    }
  }

  // Filter out any invalid keys that might have been in dslContext
  const filteredCtx: Partial<DSLContext> = {};
  for (const key of Object.keys(ctx)) {
    if (VALID_CONTEXT_KEYS.includes(key)) {
      filteredCtx[key] = ctx[key];
    }
  }

  return filteredCtx as DSLContext;
}

/**
 * Check if all required context bindings have values
 */
function hasRequiredContextValues(
  contextBindings: ContextBinding[] | undefined,
  formData: Record<string, unknown>,
  dslContext?: DSLContext
): boolean {
  if (!contextBindings || contextBindings.length === 0) {
    return true;
  }

  return contextBindings.every((binding) => {
    if (binding.sourceFieldId === FORM_CONTEXT_MARKER) {
      const value = dslContext?.[binding.contextKey];
      return value !== undefined && value !== null;
    }

    // If using entity context marker, check dslContext as fallback
    // (KeyPersons node injects directorId directly into dslContext rather than __entityContext__)
    if (binding.sourceFieldId === ENTITY_CONTEXT_MARKER) {
      return (
        dslContext?.[binding.contextKey] !== undefined &&
        dslContext?.[binding.contextKey] !== null
      );
    }

    const fieldValue = formData[binding.sourceFieldId];
    return fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
  });
}

/**
 * Whether a value looks like a repeatable module's instance array. Guards
 * entries stored before `__meta.repeatableKeys` existed: an entity field never
 * holds a list of records, so such a value is form data, not a DSL field.
 */
function isInstanceArray(value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        item !== null && typeof item === 'object' && !Array.isArray(item)
    )
  );
}

/**
 * Group field mappings by entity for batch operations
 */
function groupByEntity(
  mappings: CommendaOSFieldMapping[]
): Map<string, CommendaOSFieldMapping[]> {
  const grouped = new Map<string, CommendaOSFieldMapping[]>();

  for (const mapping of mappings) {
    const existing = grouped.get(mapping.entity) ?? [];
    existing.push(mapping);
    grouped.set(mapping.entity, existing);
  }

  return grouped;
}

// =============================================================================
// Prefill Hook
// =============================================================================

/**
 * Hook to prefill form data from Commenda OS DSL
 *
 * This hook:
 * 1. Extracts all Commenda OS field mappings from the form DSL
 * 2. Fetches data for each field from the DSL
 * 3. Handles context bindings - fields with bindings are prefilled when bound values change
 * 4. Merges with existing data (existing data takes precedence)
 * 5. Returns the prefilled data
 */
export function useCommendaOSPrefill({
  dsl,
  dslContext,
  formData = {},
  onPrefillComplete,
}: UseCommendaOSPrefillOptions) {
  const { shareToken } = useApiScope();

  const [prefillData, setPrefillData] = useState<Record<string, unknown>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Track which fields have been prefilled and with what context
  const prefilledFields = useRef<Map<string, string>>(new Map());

  // Ref to latest formData so prefillStaticFields can read it without being in deps.
  // Avoids re-running static prefill (and firing /read) on every formData change (e.g. after submit).
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  // Every mapping carries a read path, so every one of them can show the
  // entity's current value. `mode` is optional in the DSL and stored forms
  // carry values outside its documented union, so it governs writes only
  // (useCommendaOSSubmit filters for 'read-write') and must not gate reads:
  // a field the entity schema makes non-writable is exactly the one whose
  // value can only come from the entity.
  const fieldMappings = useMemo(() => extractCommendaOSFields(dsl), [dsl]);

  // Separate fields into those with and without context bindings
  const { fieldsWithBindings, fieldsWithoutBindings } = useMemo(() => {
    const withBindings: CommendaOSFieldMapping[] = [];
    const withoutBindings: CommendaOSFieldMapping[] = [];

    for (const mapping of fieldMappings) {
      if (mapping.contextBindings && mapping.contextBindings.length > 0) {
        withBindings.push(mapping);
      } else {
        withoutBindings.push(mapping);
      }
    }

    return {
      fieldsWithBindings: withBindings,
      fieldsWithoutBindings: withoutBindings,
    };
  }, [fieldMappings]);

  // Prefill a single field
  const prefillField = useCallback(
    async (
      mapping: CommendaOSFieldMapping,
      effectiveContext: DSLContext
    ): Promise<{ fieldId: string; value: unknown } | null> => {
      // Nothing to read back for a write-only field.
      if (!mapping.readPath) return null;
      try {
        const value = await CommendaOSReadHelper.readValue(
          {
            entity: mapping.entity,
            readPath: mapping.readPath,
            filters: mapping.filters,
          },
          effectiveContext,
          shareToken
        );
        if (value !== null) return { fieldId: mapping.fieldId, value };
      } catch (err) {
        console.warn(`Failed to prefill field ${mapping.fieldId}:`, err);
      }
      return null;
    },
    [shareToken]
  );

  // Prefill fields without context bindings (on mount)
  const prefillStaticFields = useCallback(async () => {
    if (!dslContext?.companyId || fieldsWithoutBindings.length === 0) {
      return {};
    }

    setIsLoading(true);
    setError(null);

    try {
      const data: Record<string, unknown> = {};

      await Promise.all(
        fieldsWithoutBindings.map(async (mapping) => {
          // Skip if already prefilled
          if (prefilledFields.current.has(mapping.fieldId)) {
            return;
          }

          const result = await prefillField(mapping, dslContext);
          if (result && !(result.fieldId in formDataRef.current)) {
            data[result.fieldId] = result.value;
            prefilledFields.current.set(result.fieldId, 'static');
          }
        })
      );

      if (Object.keys(data).length > 0) {
        setPrefillData((prev) => ({ ...prev, ...data }));
        onPrefillComplete?.(data);
      }

      return data;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      return {};
    } finally {
      setIsLoading(false);
    }
  }, [dslContext, fieldsWithoutBindings, onPrefillComplete, prefillField]);

  // Prefill fields with context bindings (when bound values change)
  const prefillBoundFields = useCallback(async () => {
    if (!dslContext?.companyId || fieldsWithBindings.length === 0) {
      return {};
    }

    const data: Record<string, unknown> = {};

    for (const mapping of fieldsWithBindings) {
      // Check if all required context values are available
      if (
        !hasRequiredContextValues(mapping.contextBindings, formData, dslContext)
      ) {
        continue;
      }

      // Build effective context with bound values
      const effectiveContext = buildEffectiveContext(
        dslContext,
        mapping.contextBindings,
        formData
      );

      // Create a cache key based on the effective context
      const contextKey = JSON.stringify(effectiveContext);

      // Skip if already prefilled with the same context
      if (prefilledFields.current.get(mapping.fieldId) === contextKey) {
        continue;
      }

      const result = await prefillField(mapping, effectiveContext);
      if (result) {
        data[result.fieldId] = result.value;
        prefilledFields.current.set(result.fieldId, contextKey);
      }
    }

    if (Object.keys(data).length > 0) {
      setPrefillData((prev) => ({ ...prev, ...data }));
      onPrefillComplete?.(data);
    }

    return data;
  }, [
    dslContext,
    fieldsWithBindings,
    formData,
    onPrefillComplete,
    prefillField,
  ]);

  // Prefill EntitySelectOrCreate template fields (key person + directorDocument) when selected entities exist
  const prefillEntitySelectOrCreateTemplateFields = useCallback(async () => {
    if (!dslContext?.companyId || !dsl) return {};

    // Each read is independent, so collect them all and run them together.
    const reads = extractEntitySelectOrCreateComponents(dsl).flatMap(
      (component) => {
        const selectedIds = formData[component.fieldId];
        if (!Array.isArray(selectedIds)) return [];
        return (selectedIds as string[]).flatMap((entityId) => {
          const numericId = Number(entityId);
          if (entityId.startsWith('new-') || Number.isNaN(numericId)) return [];
          const rowContext: DSLContext = {
            ...dslContext,
            [getEntityIdKey(component.entity)]: numericId,
          };
          return component.template.flatMap((templateField) => {
            const cos = templateField.dataSource?.commendaOS;
            if (!cos?.entity || !cos.field?.read) return [];
            return [
              {
                fieldId: `${component.fieldId}-entity-${entityId}-${templateField.fieldId || templateField.id}`,
                target: {
                  entity: cos.entity,
                  readPath: cos.field.read,
                  filters: cos.filters,
                },
                rowContext,
              },
            ];
          });
        });
      }
    );

    const results = await Promise.allSettled(
      reads.map((read) =>
        CommendaOSReadHelper.readValue(read.target, read.rowContext, shareToken)
      )
    );
    const data: Record<string, unknown> = {};
    results.forEach((result, index) => {
      const { fieldId } = reads[index];
      if (result.status === 'rejected') {
        console.warn(`[prefill] Template field ${fieldId}:`, result.reason);
      } else if (result.value !== null) {
        data[fieldId] = result.value;
      }
    });

    if (Object.keys(data).length > 0) {
      setPrefillData((prev) => ({ ...prev, ...data }));
      onPrefillComplete?.(data);
    }
    return data;
  }, [dsl, dslContext, formData, shareToken, onPrefillComplete]);

  // Combined prefill function
  const prefill = useCallback(async () => {
    const staticData = await prefillStaticFields();
    const boundData = await prefillBoundFields();
    const templateData = await prefillEntitySelectOrCreateTemplateFields();
    return { ...staticData, ...boundData, ...templateData };
  }, [
    prefillStaticFields,
    prefillBoundFields,
    prefillEntitySelectOrCreateTemplateFields,
  ]);

  // Auto-prefill static fields on mount
  useEffect(() => {
    if (dslContext?.companyId) {
      prefillStaticFields();
    }
  }, [dslContext?.companyId, prefillStaticFields]);

  // Auto-prefill bound fields when form data changes
  useEffect(() => {
    if (dslContext?.companyId && fieldsWithBindings.length > 0) {
      prefillBoundFields();
    }
  }, [
    dslContext?.companyId,
    formData,
    fieldsWithBindings.length,
    prefillBoundFields,
  ]);

  // Auto-prefill EntitySelectOrCreate template fields when selected entities change
  useEffect(() => {
    if (dslContext?.companyId && dsl) {
      prefillEntitySelectOrCreateTemplateFields();
    }
  }, [
    dslContext?.companyId,
    dsl,
    formData,
    prefillEntitySelectOrCreateTemplateFields,
  ]);

  return {
    prefillData,
    isLoading,
    error,
    prefill,
    fieldMappings,
  };
}

// =============================================================================
// Submit Hook
// =============================================================================

/**
 * Hook to submit form data to Commenda OS DSL
 *
 * This hook:
 * 1. Extracts all writable Commenda OS field mappings
 * 2. Groups fields by entity and effective context
 * 3. Handles context bindings - uses bound field values to determine entity ID
 * 4. Executes create/update operations for each entity
 */
export function useCommendaOSSubmit({
  dsl,
  dslContext,
  formData,
}: UseCommendaOSSubmitOptions) {
  const { shareToken } = useApiScope();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [results, setResults] = useState<
    Array<{
      entity: string;
      success: boolean;
      id?: number | string;
      fieldId?: string;
      error?: string;
      code?: string;
    }>
  >([]);

  // Extract writable field mappings
  const writableMappings = useMemo(() => {
    const mappings = extractCommendaOSFields(dsl).filter(
      (m) => m.mode === 'read-write'
    );
    return mappings;
  }, [dsl]);

  // Extract KeyPersons components
  const keyPersonsComponents = useMemo(() => {
    return extractKeyPersonsComponents(dsl);
  }, [dsl]);

  // Extract EntitySelectOrCreate components
  const entitySelectOrCreateComponents = useMemo(() => {
    return extractEntitySelectOrCreateComponents(dsl);
  }, [dsl]);

  // Extract BusinessEntitySelectOrCreate components
  const businessEntitySelectOrCreateComponents = useMemo(() => {
    return extractBusinessEntitySelectOrCreateComponents(dsl);
  }, [dsl]);

  // Extract DirectorDocument components
  const directorDocumentMappings = useMemo(() => {
    return extractDirectorDocumentMappings(dsl);
  }, [dsl]);

  // Submit function
  const submit = useCallback(async () => {
    const hasEntitySelectOrCreate = entitySelectOrCreateComponents.some(
      (c) => c.template.length > 0
    );

    const hasBusinessEntitySelectOrCreate =
      businessEntitySelectOrCreateComponents.length > 0;

    const hasKeyPersons = keyPersonsComponents.length > 0;

    const hasValidContext = dslContext?.companyId || dslContext?.corporationId;

    if (
      !hasValidContext ||
      (writableMappings.length === 0 &&
        !hasEntitySelectOrCreate &&
        !hasBusinessEntitySelectOrCreate &&
        !hasKeyPersons)
    ) {
      return { success: true, results: [] };
    }

    setIsSubmitting(true);
    setError(null);
    setResults([]);

    try {
      const submitResults: typeof results = [];

      // Group mappings by entity AND effective context
      // This is needed because different fields for the same entity might have different context bindings
      // For write operations, use write.entity if specified (e.g., GovernmentIdentifier reads from corporation but writes to governmentIdentifier)
      const groupedByEntityAndContext = new Map<
        string,
        {
          entity: string;
          effectiveContext: DSLContext;
          mappings: CommendaOSFieldMapping[];
        }
      >();

      for (const mapping of writableMappings) {
        // Build effective context for this mapping
        const effectiveContext = buildEffectiveContext(
          dslContext,
          mapping.contextBindings,
          formData
        );

        // Use write.entity for grouping if specified, otherwise use entity
        const writeEntity = mapping.write?.entity || mapping.entity;

        // Create a key based on write entity, effective context, and filters.
        // Filters must be included so that multiple instances of the same entity with
        // different discriminators (e.g. MAILING_ADDRESS vs REGISTERED_OFFICE)
        // are treated as separate executions rather than merged into one.
        const filterKey =
          mapping.filters && mapping.filters.length > 0
            ? JSON.stringify(
                [...mapping.filters].sort((a, b) => a.key.localeCompare(b.key))
              )
            : '';
        const contextKey = `${writeEntity}:${JSON.stringify(effectiveContext)}:${filterKey}`;

        const existing = groupedByEntityAndContext.get(contextKey);
        if (existing) {
          existing.mappings.push(mapping);
        } else {
          groupedByEntityAndContext.set(contextKey, {
            entity: writeEntity,
            effectiveContext,
            mappings: [mapping],
          });
        }
      }

      // Sort entity groups so keyPerson is processed before keyPersonRelationship.
      // When creating a new person, the keyPersonRelationship needs the directorId
      // returned by the keyPerson create call.
      const ENTITY_PRIORITY: Record<string, number> = {
        keyPerson: 0,
        keyPersonRelationship: 1,
        transferPricingBenchmark: 0,
        transferPricingPolicy: 1,
      };
      const sortedGroups = Array.from(groupedByEntityAndContext.values()).sort(
        (a, b) =>
          (ENTITY_PRIORITY[a.entity] ?? 0) - (ENTITY_PRIORITY[b.entity] ?? 0)
      );

      // Track IDs created during this submit so dependent entities can use them
      const createdIds: Record<string, number | string> = {};

      // Process each entity/context group
      for (const {
        entity,
        effectiveContext: rawEffectiveContext,
        mappings,
      } of sortedGroups) {
        // Inject any IDs created earlier in this submit batch (e.g. directorId from keyPerson create)
        const effectiveContext = { ...rawEffectiveContext };
        const entityIdKey = getEntityIdKey(entity);
        if (!effectiveContext[entityIdKey] && createdIds[entityIdKey]) {
          (effectiveContext as Record<string, unknown>)[entityIdKey] =
            createdIds[entityIdKey];
        }
        // Fetch the entity schema to look up correct update paths and parsingType for normalization
        const entitySchema = await fetchEntitySchema(entity, shareToken);

        // Check if any mapping has filters (indicates upsert operation)
        const hasFilters = mappings.some(
          (m) => m.filters && m.filters.length > 0
        );

        // Build field values for this entity
        const fields: FieldValue[] = [];
        // Location fields deferred from CREATE: saved via a follow-up UPDATE so that
        // a partially-filled address can never cause the keyPerson create to fail.
        // CreateLocationDTO has required sub-fields (addressLine1, postalCode, country)
        // that would throw a 400 if missing, killing the entire create call.
        let deferredLocationFields: FieldValue[] = [];
        let upsertFilters: CommendaOSFilter[] = [];

        // Determine whether this will be a create or update operation
        const preEntityIdKey = getEntityIdKey(entity);
        const isCreateOperation = !effectiveContext[preEntityIdKey];

        for (const mapping of mappings) {
          const rawValue = formData[mapping.fieldId];

          // Collect filters for the upsert execution. Since the grouping key now
          // includes filters, all mappings in this group share the same filters —
          // so we just take them from whichever mapping has them.
          if (mapping.filters && mapping.filters.length > 0) {
            upsertFilters = mapping.filters;
          }

          // A blank answer on create is one not given; sent as '', an optional email or phone fails validation.
          if (
            rawValue !== undefined &&
            !(isCreateOperation && rawValue === '')
          ) {
            // Special handling for key person Location component - format as locations array.
            // For CREATE operations the location is deferred to a follow-up UPDATE so that
            // required address field validation inside CreateLocationDTO never kills the
            // keyPerson create. For UPDATE operations include it directly.
            if (
              mapping.write?.entity === 'keyPerson' &&
              mapping.write?.path === 'keyPersonLocation' &&
              typeof rawValue === 'object' &&
              rawValue !== null
            ) {
              const locationObj = rawValue as {
                address?: Record<string, unknown>;
              };

              if (
                locationObj.address &&
                Object.keys(locationObj.address).length > 0
              ) {
                const locationField: FieldValue = {
                  path: 'locations',
                  value: [
                    {
                      locationTypes: ['CONTACT_ADDRESS'],
                      address: locationObj.address,
                    },
                  ],
                };
                if (isCreateOperation) {
                  // Defer to post-create update so a partial address cannot block the create.
                  deferredLocationFields.push(locationField);
                } else {
                  fields.push(locationField);
                }
              }

              continue;
            }

            // Special handling for Location component - expand location object into individual fields
            if (
              mapping.write?.entity === 'location' &&
              mapping.write?.path === 'location' &&
              typeof rawValue === 'object' &&
              rawValue !== null
            ) {
              const locationObj = rawValue as {
                address?: Record<string, unknown>;
                name?: string;
                startDate?: string;
              };

              // Expand address fields with "address." prefix, filtering out
              // non-writable keys like `id` that leak from prefilled API data
              const addressWriteFields = new Set([
                'addressLine1',
                'addressLine2',
                'addressLine3',
                'city',
                'state',
                'postalCode',
                'country',
              ]);
              if (locationObj.address) {
                for (const [key, val] of Object.entries(locationObj.address)) {
                  if (
                    addressWriteFields.has(key) &&
                    val !== undefined &&
                    val !== null &&
                    val !== ''
                  ) {
                    fields.push({ path: `address.${key}`, value: val });
                  }
                }
              }

              // Add name field if present and non-null
              if (
                locationObj.name !== undefined &&
                locationObj.name !== null &&
                locationObj.name !== ''
              ) {
                fields.push({ path: 'name', value: locationObj.name });
              }

              // Add startDate field if present
              if (
                locationObj.startDate !== undefined &&
                locationObj.startDate !== ''
              ) {
                fields.push({
                  path: 'startDate',
                  value: locationObj.startDate,
                });
              }

              continue;
            }

            // Special handling for Document component - expand { fileUrl, fileName } into individual fields
            if (
              mapping.write?.entity === 'document' &&
              mapping.write?.path === 'document'
            ) {
              // If no file has been uploaded yet, skip this field entirely
              if (typeof rawValue !== 'object' || rawValue === null) {
                continue;
              }

              const docObj = rawValue as {
                fileUrl?: string;
                fileName?: string;
                effectiveStartDate?: string;
                effectiveEndDate?: string;
              };

              if (docObj.fileUrl) {
                fields.push({ path: 'fileUrl', value: docObj.fileUrl });
              }
              if (docObj.fileName) {
                fields.push({ path: 'fileName', value: docObj.fileName });
              }
              if (docObj.effectiveStartDate) {
                fields.push({
                  path: 'effectiveStartDate',
                  value: docObj.effectiveStartDate,
                });
              }
              if (docObj.effectiveEndDate) {
                fields.push({
                  path: 'effectiveEndDate',
                  value: docObj.effectiveEndDate,
                });
              }

              continue;
            }

            // Resolve the write path based on operation type (create vs update)
            let path: string | undefined;
            let fieldDef = null;

            if (entitySchema) {
              fieldDef = mapping.readPath
                ? findFieldByReadPath(entitySchema.fields, mapping.readPath)
                : null;

              if (isCreateOperation) {
                // For create: prefer create.path, then fall back to update.path / write.path
                path =
                  fieldDef?.create?.path ||
                  mapping.createPath ||
                  mapping.updatePath ||
                  mapping.write?.path ||
                  fieldDef?.update?.path;
              } else {
                // For update: prefer update.path, then write.path
                path =
                  mapping.updatePath ||
                  mapping.write?.path ||
                  fieldDef?.update?.path;
              }

              if (!path && (fieldDef as Record<string, unknown>)?.write) {
                path = (
                  (fieldDef as Record<string, unknown>).write as {
                    path?: string;
                  }
                )?.path;
              }

              if (!path && fieldDef) {
                continue;
              }
            } else {
              path = isCreateOperation
                ? mapping.createPath ||
                  mapping.updatePath ||
                  mapping.write?.path
                : mapping.updatePath || mapping.write?.path;
            }

            // Only include fields that have an update/write path
            if (!path) {
              console.warn(
                `[useCommendaOSSubmit] No update/write path found for ${mapping.readPath ?? mapping.fieldId}, skipping`
              );
              continue;
            }

            // Normalize the value based on parsingType and field type
            const parsingType = fieldDef?.parsingType;
            const value = normalizeValueForSubmit(
              rawValue,
              parsingType,
              fieldDef?.type
            );

            fields.push({ path, value });
          }
        }

        for (const injection of CREATED_ID_FIELD_INJECTIONS[entity] ?? []) {
          const createdId = createdIds[getEntityIdKey(injection.fromEntity)];
          if (createdId === undefined) continue;
          if (fields.some((f) => f.path === injection.fieldPath)) continue;
          fields.push({ path: injection.fieldPath, value: createdId });
        }

        if (fields.length === 0 && deferredLocationFields.length === 0) {
          continue;
        }

        if (fields.length === 0 && deferredLocationFields.length > 0) {
          const entityIdKey = getEntityIdKey(entity);
          const entityId = effectiveContext[entityIdKey] as
            | number
            | string
            | undefined;
          if (entityId) {
            // Existing entity — apply location update directly
            try {
              await updateCommendaOSEntity(
                entity,
                entityId,
                deferredLocationFields,
                effectiveContext,
                shareToken
              );
            } catch (locErr) {
              console.warn(
                '[useCommendaOSSubmit] keyPerson location-only update failed:',
                locErr
              );
            }
          }
          // No directorId and no other fields: nothing to do (can't create without required fields)
          deferredLocationFields = [];
          continue;
        }

        try {
          // Check if we have an entity ID in effective context (update) or not (create)
          const entityIdKey = getEntityIdKey(entity);
          const entityId = effectiveContext[entityIdKey] as
            | number
            | string
            | undefined;

          let result;

          // keyPerson never uses upsert: filters (e.g. locationType) are UI-only. Use create or update.
          const useUpsert =
            hasFilters && upsertFilters.length > 0 && entity !== 'keyPerson';
          if (useUpsert) {
            result = await upsertCommendaOSEntity(
              entity,
              upsertFilters.map((f) => ({ key: f.key, value: f.value })),
              fields,
              effectiveContext,
              shareToken
            );
          } else if (entityId) {
            // Update existing entity
            result = await updateCommendaOSEntity(
              entity,
              entityId,
              fields,
              effectiveContext,
              shareToken
            );
          } else {
            // Create new entity
            result = await createCommendaOSEntity(
              entity,
              fields,
              effectiveContext,
              shareToken
            );
          }

          // Track created IDs so dependent entities can reference them
          if (result.result.success && result.result.id && !entityId) {
            const idKey = getEntityIdKey(entity);
            createdIds[idKey] = result.result.id;
          }

          submitResults.push({
            entity,
            success: result.result.success,
            id: result.result.id,
            error: result.result.error,
            ...(entity === 'document' && mappings[0]?.fieldId
              ? { fieldId: mappings[0].fieldId }
              : {}),
          });

          // Follow-up update for deferred location fields (keyPerson create path).
          // We do this separately so required address sub-fields in CreateLocationDTO
          // cannot block the keyPerson row from being created.
          if (
            result.result.success &&
            entity === 'keyPerson' &&
            deferredLocationFields.length > 0
          ) {
            const createdDirectorId =
              result.result.id ?? createdIds[getEntityIdKey(entity)];
            const newDirectorId =
              createdDirectorId == null ? undefined : Number(createdDirectorId);
            if (newDirectorId) {
              try {
                await updateCommendaOSEntity(
                  entity,
                  newDirectorId,
                  deferredLocationFields,
                  { ...effectiveContext, directorId: newDirectorId },
                  shareToken
                );
              } catch (locErr) {
                console.warn(
                  '[useCommendaOSSubmit] keyPerson location update failed (person was still created):',
                  locErr
                );
              }
            }
            deferredLocationFields = [];
          }
        } catch (err) {
          submitResults.push({
            entity,
            success: false,
            error: err instanceof Error ? err.message : String(err),
            code: CommendaOSFailureHelper.getErrorCode(err),
          });
        }
      }

      // When we just created a keyPerson (e.g. from a KeyPersons linked form that only has keyPerson fields),
      // link them to the corporation if we have corporation context.
      const newDirectorId =
        createdIds.directorId == null
          ? undefined
          : Number(createdIds.directorId);
      const hasCorporationContext =
        dslContext?.companyId != null && dslContext?.corporationId != null;
      if (newDirectorId != null && hasCorporationContext) {
        try {
          await executeCommendaOS(
            {
              execution: {
                type: 'create',
                entity: 'keyPersonRelationship',
                fields: [{ path: 'isDirector', value: true }],
              },
              context: {
                companyId: dslContext.companyId as number,
                corporationId: dslContext.corporationId as number,
                directorId: newDirectorId,
              },
            },
            shareToken
          );
          submitResults.push({
            entity: 'keyPersonRelationship',
            success: true,
            id: newDirectorId,
          });
        } catch (linkErr) {
          console.warn(
            '[useCommendaOSSubmit] Failed to link new key person to corporation:',
            linkErr
          );
          submitResults.push({
            entity: 'keyPersonRelationship',
            success: false,
            error: linkErr instanceof Error ? linkErr.message : String(linkErr),
            code: CommendaOSFailureHelper.getErrorCode(linkErr),
          });
        }
      }

      // Handle EntitySelectOrCreate components
      // These have template fields that need to be submitted per selected entity.
      // For keyPerson: also create keyPersonRelationship (link) when corporationId is in context.
      for (const esocComponent of entitySelectOrCreateComponents) {
        const selectedIds = formData[esocComponent.fieldId];
        if (
          !selectedIds ||
          !Array.isArray(selectedIds) ||
          selectedIds.length === 0
        ) {
          continue;
        }

        const isKeyPerson = esocComponent.entity === 'keyPerson';
        const hasCorporationContext =
          dslContext?.companyId && dslContext?.corporationId;

        // Fetch the entity schema for field lookups (keyPerson / parent entity)
        const entitySchema = await fetchEntitySchema(
          esocComponent.entity,
          shareToken
        );
        // Fetch directorDocument schema when template has directorDocument fields (for submit path resolution)
        const hasDirectorDocumentFields = esocComponent.template.some(
          (t) => t.dataSource?.commendaOS?.entity === 'directorDocument'
        );
        const directorDocumentSchema = hasDirectorDocumentFields
          ? await fetchEntitySchema('directorDocument', shareToken)
          : null;
        // Fetch keyPersonRelationship schema when template has relationship fields
        const hasKeyPersonRelationshipFields = esocComponent.template.some(
          (t) => t.dataSource?.commendaOS?.entity === 'keyPersonRelationship'
        );
        const keyPersonRelationshipSchema = hasKeyPersonRelationshipFields
          ? await fetchEntitySchema('keyPersonRelationship', shareToken)
          : null;

        // Process each selected entity
        for (const entityId of selectedIds as string[]) {
          const isNewEntity = entityId.startsWith('new-');

          // Build fields for keyPerson from template (only keyPerson entity; skip directorDocument and keyPersonRelationship)
          const fields: FieldValue[] = [];
          const relationshipFields: FieldValue[] = [];

          for (const templateField of esocComponent.template) {
            const fieldEntity = templateField.dataSource?.commendaOS?.entity;
            if (fieldEntity === 'directorDocument') {
              continue;
            }
            const entityFieldId = `${esocComponent.fieldId}-entity-${entityId}-${templateField.fieldId || templateField.id}`;
            const rawValue = formData[entityFieldId];

            if (
              rawValue === undefined ||
              rawValue === null ||
              rawValue === ''
            ) {
              continue;
            }

            const readPath = templateField.dataSource?.commendaOS?.field?.read;
            if (!readPath) continue;

            if (fieldEntity === 'keyPersonRelationship') {
              if (!keyPersonRelationshipSchema) continue;
              const fieldDef = findFieldByReadPath(
                keyPersonRelationshipSchema.fields,
                readPath
              );
              const path = fieldDef?.update?.path ?? fieldDef?.create?.path;
              if (!path) continue;
              const value = normalizeValueForSubmit(
                rawValue,
                fieldDef?.parsingType,
                fieldDef?.type
              );
              relationshipFields.push({ path, value });
              continue;
            }

            // keyPerson (or other parent-entity) field
            let path: string | undefined;
            let fieldDef = null;

            if (entitySchema) {
              fieldDef = findFieldByReadPath(entitySchema.fields, readPath);
              if (fieldDef?.update?.path) {
                path = fieldDef.update.path;
              } else if (fieldDef?.create?.path) {
                path = fieldDef.create.path;
              }
            }

            if (!path) {
              console.warn(
                `[useCommendaOSSubmit] No update/create path found for template field ${readPath}, skipping`
              );
              continue;
            }

            const parsingType = fieldDef?.parsingType;
            const value = normalizeValueForSubmit(
              rawValue,
              parsingType,
              fieldDef?.type
            );
            fields.push({ path, value });
          }

          // Key person: require at least one field for new (create); existing can link only
          if (isKeyPerson && isNewEntity && fields.length === 0) {
            continue;
          }
          if (!isKeyPerson && fields.length === 0) {
            continue;
          }

          try {
            let result;
            let directorIdForLink: number | null = null;

            if (isNewEntity) {
              // Create new entity
              result = await createCommendaOSEntity(
                esocComponent.entity,
                fields,
                dslContext,
                shareToken
              );
              directorIdForLink =
                typeof result.result.id === 'number'
                  ? result.result.id
                  : Number(result.result.id);
            } else {
              const numericEntityId = Number(entityId);
              directorIdForLink = numericEntityId;

              if (fields.length > 0) {
                // Update existing entity
                const entityContext = {
                  ...dslContext,
                  [getEntityIdKey(esocComponent.entity)]: numericEntityId,
                };

                result = await updateCommendaOSEntity(
                  esocComponent.entity,
                  numericEntityId,
                  fields,
                  entityContext,
                  shareToken
                );
              } else {
                result = { result: { success: true, id: numericEntityId } };
              }
            }

            submitResults.push({
              entity: esocComponent.entity,
              success: result.result.success,
              id: result.result.id,
              error: result.result.error,
            });

            // Key person with corporation context: link to corporation (keyPersonRelationship) and submit relationship fields
            if (
              isKeyPerson &&
              hasCorporationContext &&
              directorIdForLink != null &&
              !Number.isNaN(directorIdForLink)
            ) {
              try {
                const linkFields: FieldValue[] = [
                  { path: 'isDirector', value: true },
                  ...relationshipFields,
                ];
                const linkResult = await executeCommendaOS(
                  {
                    execution: {
                      type: 'create',
                      entity: 'keyPersonRelationship',
                      fields: linkFields,
                    },
                    context: {
                      companyId: dslContext.companyId,
                      corporationId: dslContext.corporationId,
                      directorId: directorIdForLink,
                    },
                  },
                  shareToken
                );
                submitResults.push({
                  entity: 'keyPersonRelationship',
                  success: linkResult.result.success,
                  id: directorIdForLink,
                  error: linkResult.result.error,
                });
              } catch (linkErr) {
                submitResults.push({
                  entity: 'keyPersonRelationship',
                  success: false,
                  error:
                    linkErr instanceof Error
                      ? linkErr.message
                      : String(linkErr),
                  code: CommendaOSFailureHelper.getErrorCode(linkErr),
                });
              }
            }

            // Director document template fields: upsert per (directorId, documentKind) after we have directorId
            if (
              isKeyPerson &&
              directorIdForLink != null &&
              !Number.isNaN(directorIdForLink) &&
              directorDocumentSchema &&
              dslContext?.companyId
            ) {
              const docFieldsByKind = new Map<
                string,
                { path: string; value: unknown }[]
              >();
              for (const templateField of esocComponent.template) {
                if (
                  templateField.dataSource?.commendaOS?.entity !==
                  'directorDocument'
                )
                  continue;
                const docKindFilter =
                  templateField.dataSource.commendaOS.filters?.find(
                    (f: CommendaOSFilter) => f.key === 'documentKind'
                  );
                const documentKind = docKindFilter?.value as string | undefined;
                if (!documentKind) continue;
                const entityFieldId = `${esocComponent.fieldId}-entity-${entityId}-${templateField.fieldId || templateField.id}`;
                const rawValue = formData[entityFieldId];
                if (rawValue === undefined || rawValue === null) continue;
                const readPath =
                  templateField.dataSource.commendaOS.field?.read;
                if (!readPath) continue;
                const fieldDef = directorDocumentSchema
                  ? findFieldByReadPath(directorDocumentSchema.fields, readPath)
                  : null;
                const path = fieldDef?.update?.path ?? fieldDef?.create?.path;
                if (!path) continue;
                const value = normalizeValueForSubmit(
                  rawValue,
                  fieldDef?.parsingType,
                  fieldDef?.type
                );
                const existing = docFieldsByKind.get(documentKind) ?? [];
                existing.push({ path, value });
                docFieldsByKind.set(documentKind, existing);
              }
              for (const [documentKind, docFields] of Array.from(
                docFieldsByKind
              )) {
                if (docFields.length === 0) continue;
                const hasFileUrl = docFields.some((f) => f.path === 'fileUrl');
                const hasFileName = docFields.some(
                  (f) => f.path === 'fileName'
                );
                if (!hasFileUrl || !hasFileName) {
                  // Backend requires fileUrl and fileName for directorDocument upsert
                  continue;
                }
                try {
                  const upsertResult = await upsertCommendaOSEntity(
                    'directorDocument',
                    [{ key: 'documentKind', value: documentKind }],
                    docFields as FieldValue[],
                    {
                      ...dslContext,
                      directorId: directorIdForLink,
                    },
                    shareToken
                  );
                  submitResults.push({
                    entity: 'directorDocument',
                    success: upsertResult.result.success,
                    id: directorIdForLink,
                    error: upsertResult.result.error,
                  });
                } catch (docErr) {
                  submitResults.push({
                    entity: 'directorDocument',
                    success: false,
                    error:
                      docErr instanceof Error ? docErr.message : String(docErr),
                    code: CommendaOSFailureHelper.getErrorCode(docErr),
                  });
                }
              }
            }
          } catch (err) {
            submitResults.push({
              entity: esocComponent.entity,
              success: false,
              error: err instanceof Error ? err.message : String(err),
              code: CommendaOSFailureHelper.getErrorCode(err),
            });
          }
        }
      }

      // Handle KeyPersons components
      // In view-edit mode: existing directors are already linked (only update keyPerson); new directors added via Add button get create + link. Per-person updates applied for existing.
      // In select-add mode: for each selected key person create if new, then link via keyPersonRelationship. Skip entries already submitted (__cosSubmitted).
      // A linked form only maps the fields whose data source names one. The rest
      // are the form's own questions — roles, document uploads, anything the
      // product asks but the person record does not hold — and sending them as
      // entity paths is a guess: the write is rejected on the first one the
      // entity cannot set, taking the whole person with it. The schema decides.
      const keyPersonSchema =
        keyPersonsComponents.length > 0
          ? await fetchEntitySchema('keyPerson', shareToken)
          : null;
      const unmappedPath = (
        key: string,
        prefer: 'create' | 'update'
      ): string | undefined => {
        const field = keyPersonSchema
          ? findFieldByReadPath(keyPersonSchema.fields, key)
          : null;
        if (!field) return undefined;
        return prefer === 'create'
          ? (field.create?.path ?? undefined)
          : (field.update?.path ?? field.create?.path ?? undefined);
      };

      for (const kpComponent of keyPersonsComponents) {
        const rawData = formData[kpComponent.fieldId];
        if (!rawData || typeof rawData !== 'object' || Array.isArray(rawData))
          continue;

        const kpData = rawData as {
          selectedIds?: string[];
          entries?: Record<string, Record<string, unknown>>;
        };
        const selectedIds = kpData.selectedIds ?? [];
        const entries = kpData.entries ?? {};

        if (selectedIds.length === 0) continue;

        const hasCorporationContext =
          dslContext?.companyId && dslContext?.corporationId;

        for (const personId of selectedIds) {
          const isNew = personId.startsWith('new-');
          const personEntry = entries[personId];

          const metaObj =
            personEntry?.__meta && typeof personEntry.__meta === 'object'
              ? (personEntry.__meta as Record<string, unknown>)
              : undefined;
          const alreadySubmitted = metaObj?.__cosSubmitted === true;

          // If the linked form already submitted this person's data to CommendaOS, skip entirely.
          if (alreadySubmitted) continue;

          // Field mappings stored by the linked form: { fieldId → { entity, createPath, updatePath, ... } }
          const fieldMappings = (metaObj?.fieldMappings ?? {}) as Record<
            string,
            {
              entity: string;
              createPath?: string;
              updatePath?: string;
              readPath?: string;
              filters?: Array<{ key: string; value: string }>;
            }
          >;

          // Repeatable modules in the linked form store an array of instances
          // under their own key; that is form data, not a keyPerson field.
          const repeatableKeys = new Set(
            Array.isArray(metaObj?.repeatableKeys)
              ? (metaObj.repeatableKeys as string[])
              : []
          );

          try {
            let directorId: number | undefined;

            if (isNew && personEntry) {
              const personFields: FieldValue[] = [];
              for (const [key, value] of Object.entries(personEntry)) {
                if (
                  key === '__meta' ||
                  repeatableKeys.has(key) ||
                  value === undefined ||
                  value === null ||
                  value === ''
                )
                  continue;
                const mapping = fieldMappings[key];
                if (mapping && mapping.entity === 'keyPerson') {
                  const path =
                    mapping.createPath ||
                    mapping.updatePath ||
                    mapping.readPath;
                  if (path) {
                    personFields.push({ path, value });
                  }
                } else if (!mapping && !isInstanceArray(value)) {
                  const path = unmappedPath(key, 'create');
                  if (path) personFields.push({ path, value });
                }
              }

              if (personFields.length > 0) {
                const createResult = await executeCommendaOS(
                  {
                    execution: {
                      type: 'create',
                      entity: 'keyPerson',
                      fields: personFields,
                    },
                    context: { companyId: dslContext!.companyId! },
                  },
                  shareToken
                );
                directorId =
                  createResult?.result?.id == null
                    ? undefined
                    : Number(createResult.result.id);
                submitResults.push({
                  entity: 'keyPerson',
                  success: !!directorId,
                  id: directorId,
                });
              }
            } else {
              directorId = Number(personId);

              // Update existing director with any edited fields from the linked form
              if (personEntry && directorId && dslContext?.companyId) {
                const updateFields: FieldValue[] = [];
                for (const [key, value] of Object.entries(personEntry)) {
                  if (
                    key === '__meta' ||
                    repeatableKeys.has(key) ||
                    value === undefined ||
                    value === null ||
                    value === ''
                  )
                    continue;
                  const mapping = fieldMappings[key];
                  if (mapping && mapping.entity === 'keyPerson') {
                    const path =
                      mapping.updatePath ||
                      mapping.createPath ||
                      mapping.readPath;
                    if (path) {
                      updateFields.push({ path, value });
                    }
                  } else if (!mapping && !isInstanceArray(value)) {
                    const path = unmappedPath(key, 'update');
                    if (path) updateFields.push({ path, value });
                  }
                }
                if (updateFields.length > 0) {
                  try {
                    await executeCommendaOS(
                      {
                        execution: {
                          type: 'update',
                          entity: 'keyPerson',
                          id: directorId,
                          fields: updateFields,
                        },
                        context: {
                          companyId: dslContext.companyId,
                          directorId,
                        },
                      },
                      shareToken
                    );
                    submitResults.push({
                      entity: 'keyPerson',
                      success: true,
                      id: directorId,
                    });
                  } catch (updateErr) {
                    console.warn(
                      `[useCommendaOSSubmit] Failed to update key person ${directorId}:`,
                      updateErr
                    );
                    submitResults.push({
                      entity: 'keyPerson',
                      success: false,
                      id: directorId,
                      error:
                        updateErr instanceof Error
                          ? updateErr.message
                          : String(updateErr),
                      code: CommendaOSFailureHelper.getErrorCode(updateErr),
                    });
                  }
                }

                // Update relationship fields (e.g. jobTitle/designation) for existing directors in view-edit mode.
                // In select-add mode these are sent via the create block below; here we handle the view-edit path
                // where the relationship already exists and only needs updating.
                if (
                  hasCorporationContext &&
                  kpComponent.mode === 'view-edit' &&
                  dslContext?.corporationId
                ) {
                  const relationshipUpdateFields: FieldValue[] = [];
                  for (const [key, value] of Object.entries(personEntry)) {
                    if (
                      key === '__meta' ||
                      value === undefined ||
                      value === null ||
                      value === ''
                    )
                      continue;
                    const mapping = fieldMappings[key];
                    if (mapping && mapping.entity === 'keyPersonRelationship') {
                      const path =
                        mapping.updatePath ||
                        mapping.createPath ||
                        mapping.readPath;
                      if (path) {
                        relationshipUpdateFields.push({ path, value });
                      }
                    }
                  }
                  if (relationshipUpdateFields.length > 0) {
                    try {
                      await executeCommendaOS(
                        {
                          execution: {
                            type: 'update',
                            entity: 'keyPersonRelationship',
                            id: directorId,
                            fields: relationshipUpdateFields,
                          },
                          context: {
                            companyId: dslContext.companyId!,
                            corporationId: dslContext.corporationId,
                            directorId,
                          },
                        },
                        shareToken
                      );
                      submitResults.push({
                        entity: 'keyPersonRelationship',
                        success: true,
                        id: directorId,
                      });
                    } catch (relUpdateErr) {
                      console.warn(
                        `[useCommendaOSSubmit] Failed to update key person relationship ${directorId}:`,
                        relUpdateErr
                      );
                      submitResults.push({
                        entity: 'keyPersonRelationship',
                        success: false,
                        id: directorId,
                        error:
                          relUpdateErr instanceof Error
                            ? relUpdateErr.message
                            : String(relUpdateErr),
                        code: CommendaOSFailureHelper.getErrorCode(relUpdateErr),
                      });
                    }
                  }
                }
              }
            }

            // Link to corporation via keyPersonRelationship. In select-add: always link. In view-edit: only for newly added directors (existing are already linked).
            if (
              directorId &&
              hasCorporationContext &&
              (kpComponent.mode !== 'view-edit' || isNew)
            ) {
              try {
                const relationshipFields: FieldValue[] = [
                  { path: 'isDirector', value: true },
                ];
                if (personEntry) {
                  for (const [key, value] of Object.entries(personEntry)) {
                    if (
                      key === '__meta' ||
                      value === undefined ||
                      value === null ||
                      value === ''
                    )
                      continue;
                    const mapping = fieldMappings[key];
                    if (mapping && mapping.entity === 'keyPersonRelationship') {
                      const path =
                        mapping.createPath ||
                        mapping.updatePath ||
                        mapping.readPath;
                      if (path) {
                        relationshipFields.push({ path, value });
                      }
                    }
                  }
                }
                await executeCommendaOS(
                  {
                    execution: {
                      type: 'create',
                      entity: 'keyPersonRelationship',
                      fields: relationshipFields,
                    },
                    context: {
                      companyId: dslContext!.companyId!,
                      corporationId: dslContext!.corporationId!,
                      directorId,
                    },
                  },
                  shareToken
                );
                submitResults.push({
                  entity: 'keyPersonRelationship',
                  success: true,
                  id: directorId,
                });
              } catch (linkErr) {
                console.warn(
                  `[useCommendaOSSubmit] Failed to link key person ${directorId}:`,
                  linkErr
                );
              }
            }
          } catch (err) {
            submitResults.push({
              entity: 'keyPerson',
              success: false,
              error: err instanceof Error ? err.message : String(err),
              code: CommendaOSFailureHelper.getErrorCode(err),
            });
          }
        }
      }

      // Handle BusinessEntitySelectOrCreate components
      // These have required fields (legalName, country, jurisdiction, entityType) plus extra template fields
      for (const besocComponent of businessEntitySelectOrCreateComponents) {
        const selectedIds = formData[besocComponent.fieldId];
        if (
          !selectedIds ||
          !Array.isArray(selectedIds) ||
          selectedIds.length === 0
        ) {
          continue;
        }

        // Fetch the corporation entity schema for field lookups
        const entitySchema = await fetchEntitySchema('corporation', shareToken);

        // Process each selected entity
        for (const entityId of selectedIds as string[]) {
          const isNewEntity = entityId.startsWith('new-');

          // Build fields for this entity
          const fields: FieldValue[] = [];

          if (isNewEntity) {
            // For new entities, get the creation data from form data
            const dataKey = `${besocComponent.fieldId}-entity-${entityId}-data`;
            const creationData = formData[dataKey] as
              | {
                  legalName?: string;
                  incorporationCountry?: string;
                  jurisdiction?: string;
                  businessEntityType?: string;
                  entityLegalFormId?: string;
                }
              | undefined;

            if (creationData) {
              // Add required fields
              if (creationData.legalName) {
                fields.push({
                  path: 'legalName',
                  value: creationData.legalName,
                });
              }
              if (creationData.incorporationCountry) {
                fields.push({
                  path: 'incorporationCountry',
                  value: creationData.incorporationCountry,
                });
              }
              if (creationData.jurisdiction) {
                fields.push({
                  path: 'jurisdiction',
                  value: creationData.jurisdiction,
                });
              }
              if (creationData.businessEntityType) {
                fields.push({
                  path: 'businessEntityType',
                  value: creationData.businessEntityType,
                });
              }
              // Set by the type picker, not a field of its own. The server checks it belongs to the
              // country and jurisdiction before storing it.
              if (creationData.entityLegalFormId) {
                fields.push({
                  path: 'entityLegalFormId',
                  value: creationData.entityLegalFormId,
                });
              }
            }
          }

          // Process extra template fields
          for (const templateField of besocComponent.template) {
            const entityFieldId = `${besocComponent.fieldId}-entity-${entityId}-${templateField.fieldId || templateField.id}`;
            const rawValue = formData[entityFieldId];

            if (
              rawValue !== undefined &&
              rawValue !== null &&
              rawValue !== ''
            ) {
              const readPath =
                templateField.dataSource?.commendaOS?.field?.read;
              if (!readPath) continue;

              // Look up field definition from schema
              let path: string | undefined;
              let fieldDef = null;

              if (entitySchema) {
                fieldDef = findFieldByReadPath(entitySchema.fields, readPath);
                if (fieldDef?.update?.path) {
                  path = fieldDef.update.path;
                } else if (fieldDef?.create?.path) {
                  path = fieldDef.create.path;
                }
              }

              if (!path) {
                console.warn(
                  `[useCommendaOSSubmit] No update/create path found for template field ${readPath}, skipping`
                );
                continue;
              }

              // Normalize the value
              const parsingType = fieldDef?.parsingType;
              const value = normalizeValueForSubmit(
                rawValue,
                parsingType,
                fieldDef?.type
              );

              fields.push({ path, value });
            }
          }

          if (fields.length === 0 && isNewEntity) {
            // New entity with no fields - validation error
            submitResults.push({
              entity: 'corporation',
              success: false,
              error: 'Missing required fields for new business entity',
            });
            continue;
          }

          if (fields.length === 0) {
            continue;
          }

          // Validate required fields for new entities
          if (isNewEntity) {
            const requiredFields = [
              'legalName',
              'incorporationCountry',
              'jurisdiction',
              'businessEntityType',
            ];
            const missingFields = requiredFields.filter(
              (f) => !fields.some((field) => field.path === f)
            );
            if (missingFields.length > 0) {
              submitResults.push({
                entity: 'corporation',
                success: false,
                error: `Missing required fields: ${missingFields.join(', ')}`,
              });
              continue;
            }
          }

          // Validate required additional template fields for both new and existing entities
          const missingRequiredAdditionalFields: string[] = [];
          for (const templateField of besocComponent.template) {
            const isRequired = templateField.validation?.rules?.required;
            const isRequiredEnabled =
              typeof isRequired === 'object'
                ? (isRequired as { enabled?: boolean }).enabled
                : !!isRequired;

            if (isRequiredEnabled) {
              const entityFieldId = `${besocComponent.fieldId}-entity-${entityId}-${templateField.fieldId || templateField.id}`;
              const rawValue = formData[entityFieldId];

              if (
                rawValue === undefined ||
                rawValue === null ||
                rawValue === ''
              ) {
                missingRequiredAdditionalFields.push(
                  templateField.label ||
                    templateField.fieldId ||
                    templateField.id
                );
              }
            }
          }

          if (missingRequiredAdditionalFields.length > 0) {
            submitResults.push({
              entity: 'corporation',
              success: false,
              error: `Missing required additional fields: ${missingRequiredAdditionalFields.join(', ')}`,
            });
            continue;
          }

          try {
            let result;

            if (isNewEntity) {
              // Create new corporation
              result = await createCommendaOSEntity(
                'corporation',
                fields,
                dslContext,
                shareToken
              );
            } else {
              // Update existing corporation
              const numericEntityId = Number(entityId);
              const entityContext = {
                ...dslContext,
                corporationId: numericEntityId,
              };

              result = await updateCommendaOSEntity(
                'corporation',
                numericEntityId,
                fields,
                entityContext,
                shareToken
              );
            }

            submitResults.push({
              entity: 'corporation',
              success: result.result.success,
              id: result.result.id,
              error: result.result.error,
            });
          } catch (err) {
            submitResults.push({
              entity: 'corporation',
              success: false,
              error: err instanceof Error ? err.message : String(err),
              code: CommendaOSFailureHelper.getErrorCode(err),
            });
          }
        }
      }

      // ─── DirectorDocument submit (via dsl/execute upsert: directorDocument + relationshipDocument) ─
      if (directorDocumentMappings.length > 0) {
        const directorId = dslContext?.directorId as number | undefined;
        const corporationId = dslContext?.corporationId as number | undefined;

        const createdKeyPersonResult = submitResults.find(
          (r) => r.entity === 'keyPerson' && r.success && r.id
        );
        const effectiveDirectorId =
          directorId ||
          (createdKeyPersonResult?.id == null
            ? undefined
            : Number(createdKeyPersonResult.id));

        const directorScoped = directorDocumentMappings.filter(
          (m) => !RELATIONSHIP_DOCUMENT_KINDS.has(m.documentKind)
        );
        const relationshipScoped = directorDocumentMappings.filter((m) =>
          RELATIONSHIP_DOCUMENT_KINDS.has(m.documentKind)
        );

        // Each document is its own upsert. Run sequentially per scope: relationshipDocument upserts
        // also touch the corp↔director relationship + folder provisioning, and directorDocument
        // upserts hit the same director row — so concurrent writes would race. The backend derives
        // the legacy documentType from the documentKind / documentType filter.
        const upsertDocument = async (
          entity: 'directorDocument' | 'relationshipDocument',
          filterKey: 'documentKind' | 'documentType',
          mapping: { fieldId: string; documentKind: string },
          context: DSLContext
        ) => {
          const fieldValue = formData[mapping.fieldId] as
            | { fileUrl?: string; fileName?: string }
            | undefined;
          if (!fieldValue?.fileUrl || !fieldValue?.fileName) return;
          const fields: FieldValue[] = [
            { path: 'fileUrl', value: fieldValue.fileUrl },
            { path: 'fileName', value: fieldValue.fileName },
          ];
          try {
            const res = await upsertCommendaOSEntity(
              entity,
              [{ key: filterKey, value: mapping.documentKind }],
              fields,
              context,
              shareToken
            );
            submitResults.push({
              entity,
              success: res.result.success,
              id: effectiveDirectorId,
              error: res.result.error,
            });
          } catch (err) {
            submitResults.push({
              entity,
              success: false,
              error: err instanceof Error ? err.message : String(err),
              code: CommendaOSFailureHelper.getErrorCode(err),
            });
          }
        };

        // Director-scoped docs (governmentId, panCard, …).
        if (effectiveDirectorId && directorScoped.length > 0) {
          await directorScoped.reduce<Promise<void>>(async (chain, mapping) => {
            await chain;
            await upsertDocument('directorDocument', 'documentKind', mapping, {
              ...dslContext,
              directorId: effectiveDirectorId,
            });
          }, Promise.resolve());
        }

        // Relationship-scoped docs (shareCertificate, …) — require corporationId.
        if (
          effectiveDirectorId &&
          corporationId &&
          relationshipScoped.length > 0
        ) {
          await relationshipScoped.reduce<Promise<void>>(
            async (chain, mapping) => {
              await chain;
              await upsertDocument(
                'relationshipDocument',
                'documentType',
                mapping,
                {
                  ...dslContext,
                  corporationId,
                  directorId: effectiveDirectorId,
                }
              );
            },
            Promise.resolve()
          );
        }
      }

      setResults(submitResults);

      const allSuccessful = submitResults.every((r) => r.success);
      return { success: allSuccessful, results: submitResults };
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      return { success: false, results: [], error };
    } finally {
      setIsSubmitting(false);
    }
  }, [
    dslContext,
    writableMappings,
    formData,
    keyPersonsComponents,
    entitySelectOrCreateComponents,
    businessEntitySelectOrCreateComponents,
    directorDocumentMappings,
  ]);

  return {
    submit,
    isSubmitting,
    error,
    results,
    writableMappings,
  };
}

// =============================================================================
// Combined Integration Hook
// =============================================================================

/**
 * Combined hook for Commenda OS integration
 * Provides both prefill and submit functionality
 */
export function useCommendaOSFormIntegration(options: {
  dsl: FormDSL;
  dslContext?: DSLContext;
  formData: Record<string, unknown>;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
}) {
  const { dsl, dslContext, formData, setFormData } = options;

  const prefill = useCommendaOSPrefill({
    dsl,
    dslContext,
    formData,
    onPrefillComplete: (data) => {
      setFormData((prev) => ({ ...prev, ...data }));
    },
  });

  const submit = useCommendaOSSubmit({
    dsl,
    dslContext,
    formData,
  });

  // Check if form has any Commenda OS fields
  const hasCommendaOSFields =
    prefill.fieldMappings.length > 0 || submit.writableMappings.length > 0;

  return {
    prefill,
    submit,
    hasCommendaOSFields,
    isLoading: prefill.isLoading || submit.isSubmitting,
  };
}
